/**
 * menuTree — 사이드바 메뉴 트리 (정적 정의).
 *
 * react-fastapi 의 menuStore (백엔드 동적 로드) 와 달리, 본 시스템은 정적 트리 사용.
 * 라이브 도메인(대시보드/사원/4조3교대/결재함) + 카탈로그 (시스템관리/컴포넌트/템플릿/스캐폴드).
 * 카탈로그 페이지는 placeholder 만 제공 (react-fastapi 본체 참조).
 */
export interface MenuNode {
  key: string
  label: string
  /** i18n key (cm_label.label_key). 미지정 시 label 한국어 그대로 사용. */
  labelKey?: string
  icon?: string
  path?: string
  /** true 면 사이드바에서 숨김 (라우트는 유효). */
  hidden?: boolean
  children?: MenuNode[]
}

export const MENU_TREE: MenuNode[] = [
  {
    key: 'dashboard',
    label: '대시보드',
    labelKey: 'MNU_DASHBOARD',
    path: '/dashboard',
    icon: 'dashboard'
  },
  {
    key: 'medication',
    label: '복용 관리',
    labelKey: 'MNU_MEDICATION',
    icon: 'medicine-box',
    children: [
      {
        key: 'medication-today-board',
        label: '오늘 복용 보드',
        labelKey: 'MNU_MEDICATION_TODAY',
        path: '/medication/today-board'
      },
      {
        key: 'medication-calendar',
        label: '약 달력',
        labelKey: 'MNU_MEDICATION_CALENDAR',
        path: '/medication/calendar'
      },
      {
        key: 'medication-patients',
        label: '환자 관리',
        labelKey: 'MNU_MEDICATION_PATIENTS',
        path: '/medication/patients'
      },
      {
        key: 'medication-guardians',
        label: '보호자 관리',
        labelKey: 'MNU_MEDICATION_GUARDIANS',
        path: '/medication/guardians'
      },
      {
        key: 'medication-medications',
        label: '약 마스터',
        labelKey: 'MNU_MEDICATION_CATALOG',
        path: '/medication/medications'
      },
      {
        key: 'medication-prescriptions',
        label: '처방전',
        labelKey: 'MNU_MEDICATION_PRESCRIPTION',
        path: '/medication/prescriptions'
      },
      {
        key: 'medication-presc-generator',
        label: '처방전 발행(테스트)',
        labelKey: 'MNU_MEDICATION_PRESC_GENERATOR',
        path: '/medication/prescriptions/generator'
      },
      {
        key: 'medication-schedules',
        label: '복용 스케줄',
        labelKey: 'MNU_MEDICATION_SCHEDULES',
        path: '/medication/schedules'
      },
      {
        key: 'medication-intake',
        label: '복용 이력',
        labelKey: 'MNU_MEDICATION_INTAKE',
        path: '/medication/intake'
      },
      {
        key: 'medication-devices',
        label: '디바이스 관리',
        labelKey: 'MNU_MEDICATION_DEVICES',
        path: '/medication/devices'
      },
      {
        key: 'medication-alerts',
        label: '알림 inbox',
        labelKey: 'MNU_MEDICATION_ALERTS',
        path: '/medication/alerts'
      },
      {
        key: 'medication-iot-gateway',
        label: 'IoT 게이트웨이',
        labelKey: 'MNU_MEDICATION_IOT',
        path: '/medication/iot-gateway'
      },
      {
        key: 'medication-ai-insights',
        label: 'AI 인사이트',
        labelKey: 'MNU_MEDICATION_AI',
        path: '/medication/ai-insights'
      }
    ]
  },
  // (근태 GUNTE 메뉴 employees/schedule/approval 은 2026-06 제거)
  {
    key: 'admin',
    label: '시스템관리',
    labelKey: 'MNU_ADMIN',
    icon: 'setting',
    children: [
      { key: 'admin-user', label: '사용자 관리', labelKey: 'MNU_ADMIN_USER', path: '/admin/user' },
      { key: 'admin-role', label: '역할 관리', labelKey: 'MNU_ADMIN_ROLE', path: '/admin/role' },
      { key: 'admin-menu', label: '메뉴 관리', labelKey: 'MNU_ADMIN_MENU', path: '/menu' },
      { key: 'admin-code', label: '공통 코드', labelKey: 'MNU_ADMIN_CODE', path: '/code' },
      { key: 'admin-label', label: '라벨 관리', labelKey: 'MNU_ADMIN_LABEL', path: '/label' },
      {
        key: 'admin-message',
        label: '메시지 관리',
        labelKey: 'MNU_ADMIN_MESSAGE',
        path: '/message'
      },
      {
        key: 'admin-locale',
        label: '로캘 관리',
        labelKey: 'MNU_ADMIN_LOCALE',
        path: '/admin/locale'
      },
      {
        key: 'admin-role-perm',
        label: '역할 권한',
        labelKey: 'MNU_ADMIN_PERM',
        path: '/admin/permission'
      },
      {
        key: 'admin-service-perm',
        label: '서비스 권한',
        labelKey: 'MNU_ADMIN_SVC_PERM',
        path: '/admin/service-perm'
      },
      {
        key: 'admin-item-perm',
        label: '항목 권한',
        labelKey: 'MNU_ADMIN_ITEM_PERM',
        path: '/admin/item-perm'
      },
      {
        key: 'admin-ai-config',
        label: 'AI 모델 설정',
        labelKey: 'MNU_ADMIN_AI_CONFIG',
        path: '/admin/ai-config'
      },
      {
        key: 'admin-service-log',
        label: '서비스 로그',
        labelKey: 'MNU_ADMIN_SVC_LOG',
        path: '/service-log'
      },
      { key: 'admin-audit', label: '감사 로그', labelKey: 'MNU_ADMIN_AUDIT', path: '/audit' }
    ]
  },
  {
    key: 'showcase',
    label: '컴포넌트',
    labelKey: 'MNU_SHOWCASE',
    icon: 'appstore',
    children: [
      // 쇼케이스 인덱스 메뉴 제외 (요청)
      { key: 'showcase-form', label: 'Form', path: '/showcase/form' },
      { key: 'showcase-button', label: 'Button', path: '/showcase/button' },
      { key: 'showcase-data', label: 'Data', path: '/showcase/data' },
      { key: 'showcase-panel', label: 'Panel', path: '/showcase/panel' },
      { key: 'showcase-overlay', label: 'Overlay', path: '/showcase/overlay' },
      { key: 'showcase-menu', label: 'Menu', path: '/showcase/menu' },
      { key: 'showcase-message', label: 'Message', path: '/showcase/message' },
      { key: 'showcase-misc', label: 'Misc', path: '/showcase/misc' },
      { key: 'showcase-file', label: 'File', path: '/showcase/file' },
      { key: 'showcase-chart', label: 'Chart', path: '/showcase/chart' },
      { key: 'showcase-editor', label: 'Editor', path: '/showcase/editor' }
    ]
  },
  {
    key: 'templates',
    label: '템플릿',
    labelKey: 'MNU_TEMPLATES',
    icon: 'layout',
    children: [
      // 템플릿 인덱스 메뉴 제외 (요청)
      { key: 'templates-chart', label: 'Chart', path: '/templates/chart' },
      { key: 'templates-map', label: 'Map', path: '/templates/map' },
      { key: 'templates-flow', label: 'Flow', path: '/templates/flow' },
      { key: 'templates-editor', label: 'Editor', path: '/templates/editor' },
      { key: 'templates-markdown', label: 'Markdown', path: '/templates/markdown' },
      { key: 'templates-kanban', label: 'Kanban', path: '/templates/kanban' },
      { key: 'templates-calendar', label: 'Calendar', path: '/templates/calendar' },
      { key: 'templates-pdf', label: 'PDF Viewer', path: '/templates/pdf' },
      { key: 'templates-pdf-export', label: 'PDF Export', path: '/templates/pdf-export' },
      { key: 'templates-barcode', label: 'Barcode', path: '/templates/barcode' },
      { key: 'templates-excel', label: 'Excel Import', path: '/templates/excel-import' },
      { key: 'templates-notif', label: 'Notification', path: '/templates/notification' },
      { key: 'templates-form-wizard', label: 'Form Wizard', path: '/templates/form-wizard' },
      { key: 'templates-master-slave', label: 'Master-Slave', path: '/templates/master-slave' },
      { key: 'templates-3d', label: '3D', path: '/templates/3d' },
      { key: 'templates-diagram', label: 'Diagram', path: '/templates/diagram' },
      { key: 'templates-image-crop', label: 'Image Crop', path: '/templates/image-crop' },
      { key: 'templates-large-upload', label: 'Large Upload', path: '/templates/large-upload' }
    ]
  },
  {
    key: 'scaffold',
    label: '스캐폴드 카탈로그',
    labelKey: 'MNU_SCAFFOLD',
    icon: 'block',
    children: [
      {
        key: 'scaffold-sc01',
        label: '단일 그리드 CRUD',
        labelKey: 'MNU_SC01',
        path: '/scaffold/list-crud'
      },
      {
        key: 'scaffold-sc02',
        label: '페이징 + 검색',
        labelKey: 'MNU_SC02',
        path: '/scaffold/paged-search'
      },
      {
        key: 'scaffold-sc03',
        label: 'Master-Detail Split',
        labelKey: 'MNU_SC03',
        path: '/scaffold/split-master-detail'
      },
      {
        key: 'scaffold-sc04',
        label: 'Master-Detail Tabs',
        labelKey: 'MNU_SC04',
        path: '/scaffold/tabs-master-detail'
      },
      {
        key: 'scaffold-sc05',
        label: 'Form + Lines',
        labelKey: 'MNU_SC05',
        path: '/scaffold/header-lines'
      },
      {
        key: 'scaffold-sc06',
        label: '돋보기 팝업 조회',
        labelKey: 'MNU_SC06',
        path: '/scaffold/lookup-popup'
      },
      {
        key: 'scaffold-sc07',
        label: '팝업 저장 반영',
        labelKey: 'MNU_SC07',
        path: '/scaffold/popup-save-reflect'
      },
      {
        key: 'scaffold-sc08',
        label: '트리 + 폼',
        labelKey: 'MNU_SC08',
        path: '/scaffold/tree-form'
      },
      {
        key: 'scaffold-sc09',
        label: 'Drawer 편집',
        labelKey: 'MNU_SC09',
        path: '/scaffold/drawer-edit'
      },
      {
        key: 'scaffold-sc10',
        label: '인라인 편집 (Pattern B)',
        labelKey: 'MNU_SC10',
        path: '/scaffold/inline-edit'
      }
    ]
  }
]

/**
 * 메뉴 트리에서 path 와 일치하는 노드의 label 을 재귀 탐색.
 * @param nodes 메뉴 트리 (루트 또는 children 부분 트리)
 * @param path  탐색 대상 path 문자열 (key 폴백)
 * @returns 일치하는 노드의 label, 없으면 null
 */
export const findMenuLabel = (nodes: MenuNode[], path: string): string | null => {
  for (const n of nodes) {
    if ((n.path ?? n.key) === path) return n.label
    if (n.children) {
      const r = findMenuLabel(n.children, path)
      if (r) return r
    }
  }
  return null
}

/** path 와 일치하는 노드의 labelKey 재귀 탐색 — TabBar 가 t(labelKey) 로 i18n 적용. */
export const findMenuLabelKey = (nodes: MenuNode[], path: string): string | undefined => {
  for (const n of nodes) {
    if ((n.path ?? n.key) === path) return n.labelKey
    if (n.children) {
      const r = findMenuLabelKey(n.children, path)
      if (r) return r
    }
  }
  return undefined
}
